import {
  Application,
  ChargeCardApplication,
  ChargeCardApplicationStatus,
  Query,
} from '@varo/graphql-schema-types';
import { useEffect } from 'react';
import QueryChargeCardApplicationStatuses from 'services/graphql/queries/ChargeCardApplicationStatuses.query';
import { useQuery } from 'urql';

const NOT_PENDING_STATUSES = [
  ChargeCardApplicationStatus.Declined,
  ChargeCardApplicationStatus.Unknown,
];

const isChargeCardApplication = (
  application: Application | null,
): application is ChargeCardApplication =>
  application !== null && 'chargeCardApplicationStatus' in application;

const isPendingChargeCardApplication = (application: Application | null) =>
  isChargeCardApplication(application) &&
  !NOT_PENDING_STATUSES.includes(application.chargeCardApplicationStatus);

interface UseBelieveApplicationPendingResult {
  isPending: boolean | undefined;
  fetching: boolean;
  error: Error | undefined;
  refresh: () => void;
}

const useBelieveApplicationPending = (): UseBelieveApplicationPendingResult => {
  const [{ data, fetching, error }, refetch] = useQuery<Query>({
    query: QueryChargeCardApplicationStatuses,
    requestPolicy: 'cache-and-network',
    pause: true,
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const refresh = () => {
    refetch({ requestPolicy: 'network-only' });
  };

  const getIsPending = () => {
    if (error) {
      return undefined;
    }

    return data?.applications.some(isPendingChargeCardApplication);
  };

  return { isPending: getIsPending(), fetching, error, refresh };
};

export default useBelieveApplicationPending;
